import React, { useState, useEffect } from 'react';

function OnboardingChecklist({ serverStatus, hasSkills, hasWritten, onDismiss }) {
  const [checklist, setChecklist] = useState({
    connectProject: false,
    addSkill: false,
    writeToRepo: false
  });
  const [isVisible, setIsVisible] = useState(true);
  const [isComplete, setIsComplete] = useState(false);

  // Auto-check items based on state
  useEffect(() => {
    if (serverStatus === 'active') {
      setChecklist(prev => ({ ...prev, connectProject: true }));
    }
  }, [serverStatus]);

  useEffect(() => {
    if (hasSkills) {
      setChecklist(prev => ({ ...prev, addSkill: true }));
    }
  }, [hasSkills]);

  useEffect(() => {
    if (hasWritten) {
      setChecklist(prev => ({ ...prev, writeToRepo: true }));
    }
  }, [hasWritten]);

  // Check if all items are complete
  useEffect(() => {
    const allComplete = checklist.connectProject && checklist.addSkill && checklist.writeToRepo;
    if (allComplete && !isComplete) {
      setIsComplete(true);
      setIsVisible(false);
      if (onDismiss) onDismiss();
    }
  }, [checklist, isComplete, onDismiss]);

  if (!isVisible) return null;

  // Determine current step (first uncompleted)
  const currentStep = !checklist.connectProject ? 'connectProject' :
                      !checklist.addSkill ? 'addSkill' :
                      !checklist.writeToRepo ? 'writeToRepo' : null;

  return (
    <div style={{
      position: 'absolute',
      left: '16px',
      top: '80px',
      width: '220px',
      padding: '16px',
      background: '#fafafa',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", sans-serif',
      opacity: isComplete ? 0.5 : 1,
      transition: 'opacity 1s ease',
      zIndex: 100
    }}>
      {/* Notebook header */}
      <div style={{
        marginBottom: '12px',
        paddingBottom: '8px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#6b7280',
          letterSpacing: '0.02em'
        }}>
          Getting Started
        </div>
      </div>

      {/* Checklist items */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {/* Connect project */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '3px',
            border: checklist.connectProject ? '2px solid #22c55e' : '2px solid #d1d5db',
            background: checklist.connectProject ? '#22c55e' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s ease'
          }}>
            {checklist.connectProject && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white" style={{ width: '10px', height: '10px' }}>
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <span style={{
            fontSize: '13px',
            color: checklist.connectProject ? '#6b7280' : (currentStep === 'connectProject' ? '#111827' : '#9ca3af'),
            textDecoration: checklist.connectProject ? 'line-through' : 'none',
            transition: 'all 0.2s ease'
          }}>
            Connect project
          </span>
        </div>

        {/* Add a skill */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '3px',
            border: checklist.addSkill ? '2px solid #22c55e' : '2px solid #d1d5db',
            background: checklist.addSkill ? '#22c55e' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s ease'
          }}>
            {checklist.addSkill && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white" style={{ width: '10px', height: '10px' }}>
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <span style={{
            fontSize: '13px',
            color: checklist.addSkill ? '#6b7280' : (currentStep === 'addSkill' ? '#111827' : '#9ca3af'),
            textDecoration: checklist.addSkill ? 'line-through' : 'none',
            transition: 'all 0.2s ease'
          }}>
            Add a skill
          </span>
        </div>

        {/* Write to repo */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <div style={{
            width: '16px',
            height: '16px',
            borderRadius: '3px',
            border: checklist.writeToRepo ? '2px solid #22c55e' : '2px solid #d1d5db',
            background: checklist.writeToRepo ? '#22c55e' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '2px',
            transition: 'all 0.2s ease'
          }}>
            {checklist.writeToRepo && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="white" style={{ width: '10px', height: '10px' }}>
                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
              </svg>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{
              fontSize: '13px',
              color: checklist.writeToRepo ? '#6b7280' : (currentStep === 'writeToRepo' ? '#111827' : '#9ca3af'),
              textDecoration: checklist.writeToRepo ? 'line-through' : 'none',
              transition: 'all 0.2s ease'
            }}>
              Write to repo
            </div>
            <div style={{
              fontSize: '11px',
              color: '#6b7280',
              marginTop: '2px'
            }}>
              Only skills are written. You'll be able to easily revert.
            </div>
          </div>
        </div>
      </div>

      {/* Notebook footer */}
      {isComplete && (
        <div style={{
          marginTop: '12px',
          paddingTop: '8px',
          borderTop: '1px solid #e5e7eb',
          fontSize: '11px',
          color: '#9ca3af',
          textAlign: 'center'
        }}>
          You're all set!
        </div>
      )}
    </div>
  );
}

export default OnboardingChecklist;
